import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { map, catchError, switchMap, tap, finalize } from 'rxjs/operators';
import { FeatureCollection } from '@turf/turf';
import { environment } from '../../../../../environments/environment';
import { SearchService, SearchPlotItem, isSearchPlotItem, SearchResult, SearchApiItem } from '../../../../shared/search/data-access/search.service';
import { AccessKeyService } from '../../../../shared/services/api/secret-key-api.service';
import { ActivatedRoute } from '@angular/router';
import {
  dashError,
  dashGroupCollapsed,
  dashGroupEnd,
  dashLog,
  dashWarn,
  maskToken,
  safeStringify,
} from '../../../../core/utils/dashboard-debug';
import {
  MOCK_MARKET_DATA,
  MOCK_REG_DATA,
  MOCK_LOCATION_DATA,
  MOCK_MARKET_DATA_ALT,
  MOCK_REG_DATA_ALT,
  MOCK_LOCATION_DATA_ALT
} from './mock-dashboard-data';

// Типы для Perare API
type ApiSuccessResponse<T> = {
  result: 'success' | 'error';
  msg: string | null;
  data: T;
};

export type MarketDataResponse = ApiSuccessResponse<{
  predict: Array<{
    predict_95: {
      lower_bound: number;
      upper_bound: number;
      lower_bound_per_sqm: number;
      upper_bound_per_sqm: number;
    };
    predict_99?: {
      lower_bound: number;
      upper_bound: number;
      lower_bound_per_sqm: number;
      upper_bound_per_sqm: number;
    };
    predict_per_sqm: number;
    predict_full: number;
    lower_bound_95?: number;
    upper_bound_95?: number;
    date: string;
    raw: string[] | any;
  }>;
  indexes: {
    [key: string]: number;
  };
}>;

export type RegDataResponse = ApiSuccessResponse<{
  nib: string;
  size: string;
  hakNum: string;
  hakType: string;
  hackId: number;
  centerCoord: [number, number];
}>;

export type LocationDataResponse = ApiSuccessResponse<{
  province: { id: number; name: string };
  regency: { id: number; name: string };
  kecamatan: { id: number; name: string };
  village: { id: number; name: string };
}>;

// FSV API Response types
export interface FsvApiResponse {
  result: string;
  msg: string | null;
  data: {
    fsv_idr: number;
    haircut_pct: number;
    policy_trace: {
      policy_id: string;
      matrix_row: {
        asset_type: string;
        health_bucket: string;
        health_score: number;
        base_haircut: number;
        alpha_health: number;
        beta_volatility: number;
        size_penalty_pct: number;
        rdtr_penalty_pct: number;
        vol_normalized: number;
      };
      notes: string[];
    };
    meta: {
      omv_idr: number;
      haircut_raw: number;
    };
  };
}

export interface FSVData {
  amount: string;
  amountRaw: number;
  haircutPercentage: number;
  haircutDescription: string;
  currency: string;
  policyLabel: string;
  mainText: string; // Основное предложение (bold)
  policyName: string; // Policy name для отображения с нового абзаца
}

// Reliability API Response types
export interface ReliabilityApiResponse {
  result: string;
  msg: string | null;
  data: {
    reliability_score: number;
    level: string;
    reasons: Array<{ key: string; message: string }>;
    metrics: {
      sigma_ratio: number;
      top_n_count: number;
      avg_rdtr_match: number;
      avg_data_quality: number;
      ci95_width_ratio: number;
      model_robustness: number;
      range_width_ratio: number;
    };
  };
}

export interface ReliabilityFactor {
  title: string;
  description: string;
  status: 'positive' | 'negative' | 'neutral';
}

export interface ReliabilityData {
  title: string;
  subtitle: string;
  score: {
    value: number;
    maxValue: number;
    level: 'high' | 'medium' | 'low' | 'very-low';
  };
  factors: ReliabilityFactor[];
}

// Обработанные данные для дашборда
export interface DashboardData {
  uuid: string;
  marketValue: {
    value: string;
    valueRaw: number;
    currency: string;
    landArea: string;
    landAreaRaw: number;
    perSqmExact: string;
    perSqmExactRaw: number;
    valueHigh: string;
    valueHighRaw: number;
    valueLow: string;
    valueLowRaw: number;
    perSqmHigh: string;
    perSqmHighRaw: number;
    perSqmLow: string;
    perSqmLowRaw: number;
    perSqmHigh95: string;
    perSqmHigh95Raw: number;
    perSqmLow95: string;
    perSqmLow95Raw: number;
    // volumePercent: number | null;
    volatility: number | null;
    history: Array<{
      date: string;
      value: number;
      lower: number;
      upper: number;
    }>;
  };
  subjectProperty: {
    title: string;
    area: string;
    category: string;
    address: string;
    coordinates: [number, number];
  };
  polygonCoordinates?: [number, number][];
  fsv: {
    amount: string;
    amountRaw: number;
    currency: string;
    haircutPercentage: number;
    policyLabel: string;
    haircutDescription: string;
    mainText: string;
    policyName: string;
  };
  reliability?: ReliabilityData;
  reliabilityMeta: {
    comparablesCount: number;
    priceSpreadPercentage: number;
  };
  comparables?: Array<{
    compId: string;
    address: string;
    area: number;
    totalPrice: string;
    pricePerSqm: string;
    distance: number;
    date: string;
    similarity: number;
    adjustedPrice: string;
    selected: boolean;
    coordinates: [number, number];
    url?: string;
    source?: string;
  }>;
  comparablePropertiesWarning?: {
    message: string;
    suggestions?: string[];
  };
  isochrone?: FeatureCollection | null;
}

@Injectable({
  providedIn: 'root'
})
export class DashboardDataService {
  private http = inject(HttpClient);
  private searchService = inject(SearchService);
  private accessKeyService = inject(AccessKeyService);
  private route = inject(ActivatedRoute);

  private readonly PERARE_API_BASE = `${environment.apiUrl}/perare-api/v1`;
  private readonly USE_MOCK_DASHBOARD_DATA = false;
  private readonly USE_MOCK_FALLBACK_ON_ERROR = false;

  constructor() {
    dashLog('DashboardDataService constructed', {
      baseUrl: this.PERARE_API_BASE,
      mockMode: this.USE_MOCK_DASHBOARD_DATA,
    });
  }

  /**
   * Получить API ключ текущего проекта для авторизации
   */
  private getProjectApiKey(): Observable<string> {
    const hardcodedApiKey = 'NzdjNzZlODJiMjQwZjE5ZDllNTk1MWMyNjQ0YmE0MDU=';
    dashWarn('Using hardcoded perare-api key (testing only)', {
      apiKey: maskToken(hardcodedApiKey),
    });
    return of(hardcodedApiKey);
  }

  /**
   * Создать заголовки с авторизацией для Perare API
   */
  private createAuthHeaders(apiKey: string, projectId?: string): HttpHeaders {
    let headers = new HttpHeaders({
      'Authorization': `Bearer ${apiKey}`
    });

    // Добавить x-company header если есть projectId
    if (projectId) {
      headers = headers.set('x-company', projectId);
    }

    return headers;
  }

  /**
   * Поиск участков через существующий поисковый сервис
   */
  public searchPlots(searchTerm: string): Observable<SearchPlotItem[]> {
    dashGroupCollapsed('searchPlots', {
      term: searchTerm,
      length: searchTerm?.length ?? 0,
    });

    if (!this.searchService) {
      dashError('SearchService is not available', {});
      return of([]);
    }

    // Если поисковый запрос похож на NIB (цифры), используем searchByNib
    const isNibSearch = /^\d+$/.test(searchTerm.trim());

    if (isNibSearch) {
      dashLog('detected NIB search', {});
      return this.searchService.searchByNib(searchTerm, {
        desa: '',
        kabupaten: '',
        ownershipType: ''
      }).pipe(
        map((results: SearchResult<SearchApiItem>[]) => {
          dashLog('NIB search results received', {
            count: Array.isArray(results) ? results.length : 0,
          });
          return this.processSearchResults(results);
        }),
        catchError((error) => {
          dashError('NIB search error', {
            status: (error as any)?.status,
            message: (error as any)?.message,
            response: safeStringify((error as any)?.error),
          });
          return of([]);
        }),
        // Close group on completion/error (since this method returns an Observable)
        map((value) => {
          return value;
        })
      );
    }

    dashLog('using regular string search', {});

    return this.searchService.searchByString(searchTerm).pipe(
      map((results: SearchResult<SearchApiItem>[]) => {
        dashLog('string search results received', {
          count: Array.isArray(results) ? results.length : 0,
        });
        return this.processSearchResults(results);
      }),
      catchError((error) => {
        dashError('string search error', {
          status: (error as any)?.status,
          message: (error as any)?.message,
          response: safeStringify((error as any)?.error),
        });
        return of([]);
      }),
      map((value) => {
        return value;
      })
    );
  }

  /**
   * Загрузка моковых данных для дашборда
   */
  private loadMockDashboardData(uuid: string): Observable<DashboardData> {
    dashWarn('loading MOCK dashboard data', { uuid });

    // Выбираем набор моков в зависимости от UUID
    const usePrimaryMock = uuid.includes('0199aa4f') || uuid === '04425';

    const marketData = usePrimaryMock ? MOCK_MARKET_DATA : MOCK_MARKET_DATA_ALT;
    const regData = usePrimaryMock ? MOCK_REG_DATA : MOCK_REG_DATA_ALT;
    const locationData = usePrimaryMock ? MOCK_LOCATION_DATA : MOCK_LOCATION_DATA_ALT;

    dashLog('mock data set selected', { set: usePrimaryMock ? 'PRIMARY' : 'ALTERNATIVE' });

    // Возвращаем обработанные моковые данные
    return of(this.mapToDashboardData(uuid, marketData, regData, locationData, null, null, null, null, null, null, null));
  }

  /**
   * Transform FSV API response to FSVData format
   */
  private transformFSVData(response: FsvApiResponse): FSVData {
    const { data } = response;
    const { fsv_idr, haircut_pct, policy_trace } = data;

    const formattedAmount = new Intl.NumberFormat('id-ID', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(fsv_idr);

    const haircutPercentage = Number((haircut_pct * 100).toFixed(1)); // Convert 0.1975 to 19.75 (one decimal place)

    // Определяем основной текст в зависимости от haircut
    let mainText: string;
    if (haircutPercentage <= 20) {
      // LOW haircut (≤ 20%)
      mainText = 'High liquidity profile. Forced sale value remains close to market value.';
    } else if (haircutPercentage <= 30) {
      // MEDIUM haircut (20–30%)
      mainText = 'Moderate liquidity profile. Forced sale value reflects standard market risk.';
    } else if (haircutPercentage <= 45) {
      // HIGH haircut (30–45%)
      mainText = 'Elevated risk profile. Forced sale value is significantly discounted due to limited liquidity.';
    } else {
      // VERY HIGH haircut (> 45%)
      mainText = 'Low liquidity profile. Forced sale value indicates substantial downside risk.';
    }

    const haircutDescription = policy_trace.notes.join('\n');
    const policyName = policy_trace.matrix_row.asset_type || '';

    return {
      amount: formattedAmount,
      amountRaw: fsv_idr,
      haircutPercentage,
      haircutDescription,
      currency: 'IDR',
      policyLabel: policyName, // Используем asset_type вместо policy_id
      mainText,
      policyName,
    };
  }

  /**
   * Transform Reliability API response to ReliabilityData format
   */
  private transformReliabilityData(response: ReliabilityApiResponse): ReliabilityData {
    const { data } = response;
    const { reliability_score, level, metrics, reasons } = data;

    // Определяем уровень на основе reliability_score (0.0 - 1.0):
    // High ≥ 0.80
    // Medium 0.60–0.79
    // Low < 0.60 (но >= 0.40)
    // Very low < 0.40
    let scoreLevel: 'high' | 'medium' | 'low' | 'very-low' = 'low';
    if (reliability_score >= 0.80) {
      scoreLevel = 'high';
    } else if (reliability_score >= 0.60) {
      scoreLevel = 'medium';
    } else if (reliability_score >= 0.40) {
      scoreLevel = 'low';
    } else {
      scoreLevel = 'very-low';
    }

    // Generate factors from reasons (fallback to metrics if reasons missing)
    let factors: ReliabilityFactor[] = [];

    if (reasons && reasons.length > 0) {
      const REASON_MAPPING: Record<string, { title: string; status: 'positive' | 'negative' | 'neutral' }> = {
        insufficient_data: { title: 'Insufficient Data', status: 'negative' },
        inconsistent_comps: { title: 'Inconsistent Comps', status: 'negative' },
        unstable_valuation: { title: 'Unstable Valuation', status: 'negative' },
        high_variance: { title: 'High Variance', status: 'negative' },
        wide_range: { title: 'Wide Range', status: 'negative' },
        moderate_dispersion: { title: 'Moderate Dispersion', status: 'neutral' },
        limited_robustness: { title: 'Limited Robustness', status: 'neutral' },
        wide_ci: { title: 'Wide Confidence Interval', status: 'negative' },
        consistent_comps: { title: 'Consistent Comps', status: 'positive' },
        stable_model: { title: 'Stable Model', status: 'positive' },
        tight_ci: { title: 'Tight Confidence Interval', status: 'positive' },
        narrow_range: { title: 'Narrow Range', status: 'positive' },
      };

      factors = reasons.map(reason => {
        const mapping = REASON_MAPPING[reason.key];
        const title = mapping
          ? mapping.title
          : reason.key
              .split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
        const status = mapping ? mapping.status : scoreLevel === 'high' ? 'positive' : 'neutral';

        return {
          title,
          description: reason.message,
          status,
        };
      });
    } else {
      // 1. Data Quality
      factors.push({
        title: 'Data Quality',
        description: `${(metrics.avg_data_quality * 100).toFixed(0)}% quality score`,
        status: metrics.avg_data_quality >= 0.7 ? 'positive' : metrics.avg_data_quality >= 0.4 ? 'neutral' : 'negative',
      });

      // 2. Data Points (Sample Size)
      factors.push({
        title: 'Data Points',
        description: `${metrics.top_n_count.toLocaleString()} comparables`,
        status: metrics.top_n_count > 100 ? 'positive' : metrics.top_n_count > 30 ? 'neutral' : 'negative',
      });

      // 3. Model Robustness
      factors.push({
        title: 'Model Robustness',
        description: `${(metrics.model_robustness * 100).toFixed(0)}% stability`,
        status: metrics.model_robustness >= 0.7 ? 'positive' : metrics.model_robustness >= 0.4 ? 'neutral' : 'negative',
      });

      // 4. RDTR Match (Spatial Planning)
      factors.push({
        title: 'Spatial Planning Match',
        description: `${(metrics.avg_rdtr_match * 100).toFixed(0)}% match rate`,
        status: metrics.avg_rdtr_match >= 0.8 ? 'positive' : metrics.avg_rdtr_match >= 0.5 ? 'neutral' : 'negative',
      });
    }

    return {
      title: 'Reliability Score',
      subtitle: `Assessment: ${level.charAt(0).toUpperCase() + level.slice(1).toLowerCase()}`,
      score: {
        value: Math.round(reliability_score * 100),
        maxValue: 100,
        level: scoreLevel,
      },
      factors: factors,
    };
  }

  /**
   * Загрузка всех данных для дашборда по UUID участка
   */
  public loadDashboardData(uuid: string): Observable<DashboardData> {
    dashGroupCollapsed('loadDashboardData', {
      uuid,
      mockMode: this.USE_MOCK_DASHBOARD_DATA,
      baseUrl: this.PERARE_API_BASE,
    });

    if (this.USE_MOCK_DASHBOARD_DATA) {
      dashWarn('forced mock data mode enabled', {});
      return this.loadMockDashboardData(uuid).pipe(
        tap((data) =>
          dashLog('loadDashboardData (mock) OK (summary)', {
            uuid: (data as any)?.uuid,
            marketValueRaw: (data as any)?.marketValue?.valueRaw,
          })
        )
      );
    }

    // Сначала получаем API ключ, затем делаем запросы
    return this.getProjectApiKey().pipe(
      switchMap(apiKey => {
        const projectId = this.route.snapshot.paramMap.get('project_id') ||
                         this.route.parent?.snapshot.paramMap.get('project_id') ||
                         this.route.parent?.parent?.snapshot.paramMap.get('project_id') || '';

        dashLog('loading real dashboard data', {
          projectId,
          apiKey: maskToken(apiKey),
        });

        // Параллельные запросы к разным API endpoints с авторизацией
        return forkJoin({
          marketData: this.getMarketData(uuid, apiKey, projectId),
          regData: this.getRegData(uuid, apiKey, projectId),
          locationData: this.getLocationData(uuid, apiKey, projectId)
        });
      }),
      map(({ marketData, regData, locationData }) => {
        dashLog('all perare-api calls completed', {
          marketPredictCount: (marketData as any)?.data?.predict?.length ?? 0,
          hasReg: !!(regData as any)?.data,
          hasLocation: !!(locationData as any)?.data,
        });

        return this.mapToDashboardData(uuid, marketData, regData, locationData, null, null, null, null, null, null, null);
      }),
      tap((data) =>
        dashLog('loadDashboardData (real) OK (summary)', {
          uuid: (data as any)?.uuid,
          marketValueRaw: (data as any)?.marketValue?.valueRaw,
          coords: (data as any)?.subjectProperty?.coordinates,
        })
      ),
      catchError((error) => {
        dashError('error loading real data', {
          status: (error as any)?.status,
          message: (error as any)?.message,
          response: safeStringify((error as any)?.error),
        });

        if (this.USE_MOCK_FALLBACK_ON_ERROR) {
          dashWarn('falling back to MOCK data', {});
          return this.loadMockDashboardData(uuid);
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * Build dashboard data from Dashboard Report detail (dashboards API).
   * Report payload contains executed widget endpoints and their data.
   */
  public buildDashboardDataFromReport(report: unknown): DashboardData {
    const r: any = report as any;

    // Handle new format: report.data.data.widgets vs old format: report.data.widgets
    const plotUuid: string | undefined = r?.plotUuid ?? r?.data?.plotUuid ?? r?.data?.data?.plotUuid;
    const widgets: Record<string, any> | undefined = r?.data?.data?.widgets ?? r?.data?.widgets ?? r?.widgets;
    const status: string | undefined = r?.status ?? r?.data?.status;


    dashLog('buildDashboardDataFromReport', {
      hasPlotUuid: !!plotUuid,
      status,
      widgetsCount: widgets ? Object.keys(widgets).length : 0,
      reportKeys: Object.keys(r || {}),
      dataKeys: r?.data ? Object.keys(r.data) : [],
      widgetIds: widgets ? Object.keys(widgets) : [],
    });

    if (status && status !== 'success') {
      dashWarn('report status is not success', { status });
      throw new Error(`Report status: ${status}`);
    }

    if (!plotUuid) {
      throw new Error('Report missing plotUuid');
    }
    if (!widgets || typeof widgets !== 'object') {
      throw new Error('Report missing widgets payload');
    }

    const marketPayload = this.extractEndpointPayload(widgets, 'market-data');
    const volatilityPayload = this.extractEndpointPayload(widgets, 'volatility');

    // reg-data and location are optional - if not found, use null/empty
    const regPayload = this.extractEndpointPayload(widgets, 'reg-data');
    const locationPayload = this.extractEndpointPayload(widgets, 'location');

    // Extract pricing data for comparables from separate endpoints
    // Try new endpoint names first, then fallback to old 'pricing-combined' for backward compatibility
    const pricingCombinedPayload = this.extractEndpointPayload(widgets, 'pricing/combined') ||
                                    this.extractEndpointPayload(widgets, 'pricing-combined');
    // Try multiple endpoint name variations for pricing data
    const pricingComparisonsPayload =
      this.extractEndpointPayload(widgets, 'pricing/comparisons') ||
      this.extractEndpointPayload(widgets, 'pricing-comparisons') ||
      this.extractEndpointPayload(widgets, 'comparisons');

    const pricingAdjustmentsPayload =
      this.extractEndpointPayload(widgets, 'pricing/adjustments') ||
      this.extractEndpointPayload(widgets, 'pricing-adjustments') ||
      this.extractEndpointPayload(widgets, 'adjustments');

    // Log available pricing endpoints for debugging with detailed structure
    dashLog('pricing endpoints extraction', {
      hasComparisonsPayload: !!pricingComparisonsPayload,
      hasAdjustmentsPayload: !!pricingAdjustmentsPayload,
      hasCombinedPayload: !!pricingCombinedPayload,
      comparisonsPayloadKeys: pricingComparisonsPayload ? Object.keys(pricingComparisonsPayload as any).slice(0, 10) : [],
      adjustmentsPayloadKeys: pricingAdjustmentsPayload ? Object.keys(pricingAdjustmentsPayload as any).slice(0, 10) : []
    });

    // Detailed structure logging for verification
    // Note: payloads from extractEndpointPayload are already unwrapped (they return .data),
    // so we need to check for nested structure: payload.comparisons or payload (if it's already an array)
    if (pricingComparisonsPayload) {
      const comps = (pricingComparisonsPayload as any)?.comparisons ||
                    (Array.isArray(pricingComparisonsPayload) ? pricingComparisonsPayload : []);
      if (comps.length > 0) {
        const firstComp = comps[0];
        dashLog('pricing/comparisons structure verification (from payload)', {
          hasComparisonsField: !!(pricingComparisonsPayload as any)?.comparisons,
          isArray: Array.isArray(pricingComparisonsPayload),
          totalComparisons: comps.length,
          firstComparisonStructure: {
            hasCompId: 'comp_id' in firstComp,
            hasAddress: 'address' in firstComp,
            hasLandSize: 'land_size' in firstComp,
            hasPricePpm2: 'price_ppm2' in firstComp,
            hasDistanceM: 'distance_m' in firstComp,
            hasLastSeen: 'last_seen' in firstComp,
            hasSimScore: 'sim_score' in firstComp,
            hasLat: 'lat' in firstComp,
            hasLon: 'lon' in firstComp,
            hasUrl: 'url' in firstComp,
            hasSource: 'source' in firstComp,
            sampleValues: {
              comp_id: firstComp.comp_id,
              address: firstComp.address,
              land_size: firstComp.land_size,
              price_ppm2: firstComp.price_ppm2,
              distance_m: firstComp.distance_m,
              last_seen: firstComp.last_seen,
              sim_score: firstComp.sim_score
            }
          }
        });
      }
    }

    if (pricingAdjustmentsPayload) {
      const adjs = (pricingAdjustmentsPayload as any)?.adjustments ||
                   (Array.isArray(pricingAdjustmentsPayload) ? pricingAdjustmentsPayload : []);
      if (adjs.length > 0) {
        const firstAdj = adjs[0];
        dashLog('pricing/adjustments structure verification (from payload)', {
          hasAdjustmentsField: !!(pricingAdjustmentsPayload as any)?.adjustments,
          isArray: Array.isArray(pricingAdjustmentsPayload),
          totalAdjustments: adjs.length,
          firstAdjustmentStructure: {
            hasCompId: 'comp_id' in firstAdj,
            hasAdjPricePpm2: 'adj_price_ppm2' in firstAdj,
            sampleValues: {
              comp_id: firstAdj.comp_id,
              adj_price_ppm2: firstAdj.adj_price_ppm2
            }
          }
        });
      }
    }

    // Extract FSV data
    const fsvPayload = this.extractEndpointPayload(widgets, 'fsv-quote');

    // Extract Reliability data
    const reliabilityPayload = this.extractEndpointPayload(widgets, 'reliability');
    dashLog('reliability payload extracted', {
      hasPayload: !!reliabilityPayload,
      payloadKeys: reliabilityPayload ? Object.keys(reliabilityPayload) : [],
      payloadSample: reliabilityPayload ? JSON.stringify(reliabilityPayload).substring(0, 200) : null
    });
    if (!reliabilityPayload) {
      dashWarn('⚠️ Reliability payload NOT FOUND in report widgets', {
        widgetIds: Object.keys(widgets),
        widgetEndpoints: Object.keys(widgets).map(id => ({
          id,
          endpoints: Object.keys(widgets[id]?.endpoints || {})
        }))
      });
    }

    const missing: string[] = [];
    if (!marketPayload) missing.push('market-data');

    if (missing.length) {
      dashWarn('report missing required endpoints', { missing });
      throw new Error(`Report missing required endpoints: ${missing.join(', ')}`);
    }

    const marketData: any = { data: marketPayload };
    const volatilityData: any = volatilityPayload ? { data: volatilityPayload } : null;
    const regData: any = regPayload ? { data: regPayload } : null;
    const locationData: any = locationPayload ? { data: locationPayload } : null;
    const pricingCombinedData: any = pricingCombinedPayload ? { data: pricingCombinedPayload } : null;
    const pricingComparisonsData: any = pricingComparisonsPayload ? { data: pricingComparisonsPayload } : null;
    const pricingAdjustmentsData: any = pricingAdjustmentsPayload ? { data: pricingAdjustmentsPayload } : null;
    const fsvData: any = fsvPayload ? { data: fsvPayload } : null;
    const reliabilityData: any = reliabilityPayload ? { data: reliabilityPayload } : null;

    // Extract polygon geometry from report data
    const reportData = (report as any)?.data?.data || (report as any)?.data;
    const geometry = reportData?.geometry;
    const polygonCoordinates: [number, number][] | null = geometry?.coordinates?.[0] || null;

    // Extract isochrone data from report
    const isochroneData = reportData?.isochrone?.data || null;
    dashLog('extracting isochrone from report', {
      hasIsochrone: !!isochroneData,
      isFeatureCollection: isochroneData?.type === 'FeatureCollection',
      featuresCount: isochroneData?.features?.length || 0
    });

    const dashboardData = this.mapToDashboardData(plotUuid, marketData, regData, locationData, pricingCombinedData, pricingComparisonsData, pricingAdjustmentsData, fsvData, reliabilityData, polygonCoordinates, volatilityData, isochroneData);

    dashLog('built dashboard data from report (summary)', {
      uuid: dashboardData.uuid,
      marketValueRaw: dashboardData.marketValue?.valueRaw,
      coords: dashboardData.subjectProperty?.coordinates,
      hasReliability: !!dashboardData.reliability,
      reliabilityScore: dashboardData.reliability?.score?.value,
      reliabilityLevel: dashboardData.reliability?.score?.level,
    });

    return dashboardData;
  }

  private extractEndpointPayload(widgets: Record<string, any>, endpointName: string): unknown | null {
    const unwrap = (raw: any): any => {
      if (raw == null) return null;
      if (typeof raw === 'object' && raw && 'data' in raw && 'result' in raw) {
        return raw.data;
      }
      return raw;
    };

    // Debug: log all available endpoints for reliability widget
    if (endpointName === 'reliability') {
      dashLog('searching for reliability endpoint', {
        widgetIds: Object.keys(widgets),
        endpointName
      });
      for (const widgetId of Object.keys(widgets)) {
        const widget = widgets[widgetId];
        const endpoints = widget?.endpoints;
        if (endpoints) {
          dashLog('widget endpoints', {
            widgetId,
            endpointNames: Object.keys(endpoints),
            widgetTitle: widget?.title
          });
        }
      }
    }

    // 1) Exact endpoint name match
    for (const widgetId of Object.keys(widgets)) {
      const widget = widgets[widgetId];
      const endpoint = widget?.endpoints?.[endpointName];

      if (!endpoint) continue;
      if (endpoint?.success === false) continue;

      const payload = unwrap(endpoint?.data);
      if (payload == null) continue;

      dashLog('endpoint payload resolved (exact match)', { widgetId, endpointName });
      return payload;
    }

    // 2) Heuristic fallback (helps when backend uses different endpoint naming)
    const isMarketPayload = (p: any) => !!p && Array.isArray(p.predict);
    const isRegPayload = (p: any) =>
      !!p &&
      (Array.isArray(p.centerCoord) ||
        (typeof p.nib === 'string' && typeof p.size === 'string') ||
        typeof p.hakNum === 'string');
    const isLocationPayload = (p: any) => !!p && !!p.province && !!p.regency && !!p.kecamatan;
    const isReliabilityPayload = (p: any) =>
      !!p &&
      typeof p.reliability_score === 'number' &&
      typeof p.level === 'string' &&
      !!p.metrics &&
      typeof p.metrics === 'object';
    const isVolatilityPayload = (p: any) => !!p && typeof p.volatility === 'number';

    const predicate =
      endpointName === 'market-data'
        ? isMarketPayload
        : endpointName === 'reg-data'
          ? isRegPayload
          : endpointName === 'location'
            ? isLocationPayload
            : endpointName === 'reliability'
              ? isReliabilityPayload
              : endpointName === 'volatility'
                ? isVolatilityPayload
                : null;

    if (!predicate) return null;

    for (const widgetId of Object.keys(widgets)) {
      const widget = widgets[widgetId];
      const endpoints = widget?.endpoints;
      if (!endpoints || typeof endpoints !== 'object') continue;

      for (const [name, endpoint] of Object.entries(endpoints)) {
        if ((endpoint as any)?.success === false) continue;
        const payload = unwrap((endpoint as any)?.data);
        if (payload == null) continue;
        if (!predicate(payload)) continue;

        dashWarn('endpoint payload resolved (heuristic match)', {
          requested: endpointName,
          matched: name,
          widgetId,
        });
        return payload;
      }
    }

    return null;
  }

  /**
   * Получение рыночных данных
   */
  private getMarketData(uuid: string, apiKey: string, projectId: string): Observable<MarketDataResponse> {
    const url = `${this.PERARE_API_BASE}/market-data?uuid=${uuid}`;
    dashLog('calling market-data', { projectId, url });

    const headers = this.createAuthHeaders(apiKey, projectId);

    return this.http.get<MarketDataResponse>(url, { headers }).pipe(
      map(response => {
        dashLog('market-data response (summary)', {
          result: (response as any)?.result,
          predictCount: (response as any)?.data?.predict?.length ?? 0,
        });
        return response;
      }),
      catchError(error => {
        dashError('market-data error', {
          status: (error as any)?.status,
          message: (error as any)?.message,
          response: safeStringify((error as any)?.error),
        });
        throw error;
      })
    );
  }

  /**
   * Получение данных регистрации участка
   */
  private getRegData(uuid: string, apiKey: string, projectId: string): Observable<RegDataResponse> {
    const url = `${this.PERARE_API_BASE}/reg-data?uuid=${uuid}`;
    dashLog('calling reg-data', { projectId, url });

    const headers = this.createAuthHeaders(apiKey, projectId);

    return this.http.get<RegDataResponse>(url, { headers }).pipe(
      map(response => {
        dashLog('reg-data response (summary)', {
          result: (response as any)?.result,
          hasData: !!(response as any)?.data,
          hakType: (response as any)?.data?.hakType,
        });
        return response;
      }),
      catchError(error => {
        dashError('reg-data error', {
          status: (error as any)?.status,
          message: (error as any)?.message,
          response: safeStringify((error as any)?.error),
        });
        throw error;
      })
    );
  }

  /**
   * Получение данных о местоположении
   */
  private getLocationData(uuid: string, apiKey: string, projectId: string): Observable<LocationDataResponse> {
    const url = `${this.PERARE_API_BASE}/location?uuid=${uuid}`;
    dashLog('calling location', { projectId, url });

    const headers = this.createAuthHeaders(apiKey, projectId);

    return this.http.get<LocationDataResponse>(url, { headers }).pipe(
      map(response => {
        dashLog('location response (summary)', {
          result: (response as any)?.result,
          province: (response as any)?.data?.province?.name,
          regency: (response as any)?.data?.regency?.name,
        });
        return response;
      }),
      catchError(error => {
        dashError('location error', {
          status: (error as any)?.status,
          message: (error as any)?.message,
          response: safeStringify((error as any)?.error),
        });
        throw error;
      })
    );
  }

  /**
   * Преобразование API данных в формат дашборда
   */
  private mapToDashboardData(
    uuid: string,
    marketData: MarketDataResponse,
    regData: RegDataResponse | null,
    locationData: LocationDataResponse | null,
    pricingCombinedData?: any,
    pricingComparisonsData?: any,
    pricingAdjustmentsData?: any,
    fsvApiResponse?: any,
    reliabilityApiResponse?: any,
    polygonCoordinates?: [number, number][] | null,
    volatilityApiResponse?: any,
    isochroneData?: FeatureCollection | null
  ): DashboardData {
    const marketPayload = marketData?.data;
    const regInfo = regData?.data;
    const location = locationData?.data;

    if (!marketPayload || !marketPayload.predict?.length) {
      throw new Error('Market data payload is empty');
    }

    // regData and locationData are optional - use empty defaults if not provided
    if (!regInfo) {
      dashWarn('Registration data not available, using defaults', {});
      // Use default empty values
    }
    if (!location) {
      dashWarn('Location data not available, using defaults', {});
      // Use default empty values
    }

    // Берем последний элемент массива (последняя дата)
    const latestPredict = marketPayload.predict[marketPayload.predict.length - 1];

    // Format number with Indonesian locale (dots as thousand separators, no currency symbol)
    const formatIDR = (value: number): string => {
      return new Intl.NumberFormat('id-ID', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value || 0);
    };

    // Extract comparables data from separate endpoints: pricing/combined, pricing/comparisons, pricing/adjustments
    // Try new separate endpoints first, fallback to old pricing-combined structure for backward compatibility
    dashLog('extracting comparables data', {
      hasPricingComparisonsData: !!pricingComparisonsData?.data,
      hasPricingAdjustmentsData: !!pricingAdjustmentsData?.data,
      hasPricingCombinedData: !!pricingCombinedData?.data,
      comparisonsDataType: pricingComparisonsData?.data ? typeof pricingComparisonsData.data : 'none',
      adjustmentsDataType: pricingAdjustmentsData?.data ? typeof pricingAdjustmentsData.data : 'none',
      pricingCombinedKeys: pricingCombinedData?.data ? Object.keys(pricingCombinedData.data) : []
    });

    // Extract comparisons: new endpoints have data.comparisons, old endpoint has data.comparisons
    const comparisonsData = pricingComparisonsData?.data?.comparisons ||
                           (Array.isArray(pricingComparisonsData?.data) ? pricingComparisonsData?.data : null) ||
                           pricingCombinedData?.data?.comparisons ||
                           [];

    // Extract adjustments: new endpoints have data.adjustments, old endpoint has data.adjustments
    const adjustmentsArray = pricingAdjustmentsData?.data?.adjustments ||
                            (Array.isArray(pricingAdjustmentsData?.data) ? pricingAdjustmentsData?.data : null) ||
                            pricingCombinedData?.data?.adjustments ||
                            [];
    const pricingData = pricingCombinedData?.data || {};
    const selectedComparisons = pricingData?.selected_comparisons || [];
    const pricingMetadata = pricingData?.metadata || {};

    // Extract warning from pricing-combined payload if available
    // pricingCombinedData?.data is the actual payload data, so warning should be in pricingData?.warning
    const pricingCombinedWarning = pricingData?.warning || null;
    
    const comparablePropertiesWarning = pricingCombinedWarning ? {
      message: pricingCombinedWarning.message || null, // Will be translated in template
      suggestions: pricingCombinedWarning.suggestions || []
    } : undefined;

    // Ensure comparisons is an array
    const comparisons = Array.isArray(comparisonsData) ? comparisonsData : [];

    dashLog('comparables data extracted', {
      comparisonsCount: comparisons.length,
      adjustmentsCount: Array.isArray(adjustmentsArray) ? adjustmentsArray.length : 0,
      selectedComparisonsCount: selectedComparisons.length,
      firstComparisonSample: comparisons.length > 0 ? {
        comp_id: comparisons[0]?.comp_id,
        address: comparisons[0]?.address,
        land_size: comparisons[0]?.land_size,
        price_ppm2: comparisons[0]?.price_ppm2,
        distance_m: comparisons[0]?.distance_m,
        last_seen: comparisons[0]?.last_seen,
        sim_score: comparisons[0]?.sim_score
      } : null
    });

    // Detailed structure verification for comparables (checking all required fields)
    if (comparisons.length > 0) {
      const firstComp = comparisons[0];
      dashLog('pricing comparables structure verification', {
        dataSource: pricingComparisonsData?.data ? 'pricing/comparisons (new)' : pricingCombinedData?.data ? 'pricing-combined (fallback)' : 'none',
        totalComparisons: comparisons.length,
        structureCheck: {
          hasCompId: 'comp_id' in firstComp,
          hasAddress: 'address' in firstComp,
          hasLandSize: 'land_size' in firstComp,
          hasPricePpm2: 'price_ppm2' in firstComp,
          hasDistanceM: 'distance_m' in firstComp,
          hasLastSeen: 'last_seen' in firstComp,
          hasSimScore: 'sim_score' in firstComp,
          hasLat: 'lat' in firstComp,
          hasLon: 'lon' in firstComp,
          hasUrl: 'url' in firstComp,
          hasSource: 'source' in firstComp
        },
        sampleValues: {
          comp_id: firstComp.comp_id,
          address: firstComp.address,
          land_size: firstComp.land_size,
          price_ppm2: firstComp.price_ppm2,
          distance_m: firstComp.distance_m,
          last_seen: firstComp.last_seen,
          sim_score: firstComp.sim_score,
          lat: firstComp.lat,
          lon: firstComp.lon
        }
      });
    }

    // Detailed structure verification for adjustments
    if (Array.isArray(adjustmentsArray) && adjustmentsArray.length > 0) {
      const firstAdj = adjustmentsArray[0];
      dashLog('pricing adjustments structure verification', {
        dataSource: pricingAdjustmentsData?.data ? 'pricing/adjustments (new)' : pricingCombinedData?.data ? 'pricing-combined (fallback)' : 'none',
        totalAdjustments: adjustmentsArray.length,
        structureCheck: {
          hasCompId: 'comp_id' in firstAdj,
          hasAdjPricePpm2: 'adj_price_ppm2' in firstAdj
        },
        sampleValues: {
          comp_id: firstAdj.comp_id,
          adj_price_ppm2: firstAdj.adj_price_ppm2
        }
      });
    }

    // Create a set of selected IDs for O(1) lookup
    const selectedIds = new Set(selectedComparisons.map((c: any) => c.comp_id));

    // Build adjustments map - store both with and without prefix for flexible lookup
    const adjustmentsMap = new Map<string, any>();
    if (Array.isArray(adjustmentsArray)) {
      adjustmentsArray.forEach((adj: any) => {
        const compIdWithPrefix = adj.comp_id;
        const compIdWithoutPrefix = compIdWithPrefix?.replace(/^lamudi_/, '');

        // Store both variants for flexible lookup
        if (compIdWithPrefix) {
          adjustmentsMap.set(compIdWithPrefix, adj);
        }
        if (compIdWithoutPrefix && compIdWithoutPrefix !== compIdWithPrefix) {
          adjustmentsMap.set(compIdWithoutPrefix, adj);
        }
      });
    }

    // Calculate market data bounds (used for price spread fallback and other calculations)
    const upperBoundTotal = latestPredict.predict_95?.upper_bound || 0;
    const lowerBoundTotal = latestPredict.predict_95?.lower_bound || 0;

    // Use metadata from pricing-combined if available, otherwise calculate from market data
    let comparablesCount = pricingMetadata.selected_count || selectedComparisons.length || 0;
    let priceSpreadPercentage = 0;

    if (selectedComparisons.length > 0) {
      // Calculate price spread from comparables
      const prices = selectedComparisons
        .map((comp: any) => comp.price_ppm2)
        .filter((p: number) => p > 0);
      if (prices.length > 0) {
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);
        priceSpreadPercentage = minPrice > 0
          ? Number((((maxPrice - minPrice) / minPrice) * 100).toFixed(1))
          : 0;
      }
    } else {
      // Fallback to market data calculation
      priceSpreadPercentage =
        upperBoundTotal && lowerBoundTotal
          ? Number(
              (((upperBoundTotal - lowerBoundTotal) / lowerBoundTotal) * 100).toFixed(1)
            )
          : 0;
    }

    // Map comparables to DashboardData format
    // IMPORTANT: Use all comparisons (not only selected_comparisons) as per transformation suggestion
    // Selected flag is determined by checking if comp_id is in selectedIds set
    const comparables = comparisons.map((comp: any) => {
      // Look up adjustment using pattern: ${source}_${comp_id} (universal pattern)
      const adjustmentKey = comp.source ? `${comp.source}_${comp.comp_id}` : `lamudi_${comp.comp_id}`;
      const adjustment = adjustmentsMap.get(adjustmentKey) ||
                        adjustmentsMap.get(comp.comp_id) ||
                        adjustmentsMap.get(`lamudi_${comp.comp_id}`);

      // Calculate total price
      const totalPriceRaw = comp.price_ppm2 * comp.land_size;

      // Determine adjusted price - if adjustment exists, use adj_price_ppm2 * land_size, otherwise use regular price
      const adjustedPricePerSqm = adjustment?.adj_price_ppm2 || comp.price_ppm2 || 0;
      const adjustedPriceRaw = adjustment
        ? adjustment.adj_price_ppm2 * comp.land_size
        : totalPriceRaw; // Fallback to regular total price if no adjustment

      // Format date from last_seen (as per specification)
      let dateStr = 'N/A';
      if (comp.last_seen) {
        try {
          const dateObj = new Date(comp.last_seen);
          dateStr = dateObj.toLocaleDateString('id-ID', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          });
        } catch (e) {
          // If date parsing fails, keep 'N/A'
        }
      }

      // Use address from comp.address (as per specification)
      const address = comp.address || `${comp.source?.charAt(0).toUpperCase() + comp.source?.slice(1) || 'Source'} Listing`;

      return {
        compId: comp.comp_id || '',
        address,
        area: comp.land_size || 0,
        totalPrice: formatIDR(totalPriceRaw), // Format: "Rp 24.999.999.960" (with currency symbol, matches transformation suggestion)
        pricePerSqm: formatIDR(comp.price_ppm2 || 0),
        distance: Math.round(comp.distance_m || 0), // Round to integer meters
        date: dateStr, // Format: "23 Nov 2025" from last_seen with 'id-ID' locale
        similarity: Math.round((comp.sim_score || 0) * 100), // Convert 0.73 to 73
        adjustedPrice: formatIDR(adjustedPriceRaw), // Uses adj_price_ppm2 from adjustments
        selected: selectedIds.has(comp.comp_id), // Check if comp_id is in selectedIds set
        coordinates: [comp.lat || 0, comp.lon || 0] as [number, number], // [lat, lon] as per transformation suggestion
        url: comp.url,
        source: comp.source
      };
    });

    // Transform FSV data from API if available, otherwise use fallback calculation
    let fsvData: FSVData;
    if (fsvApiResponse?.data) {
      try {
        // Unwrap the response if it's wrapped
        const fsvResponseData = fsvApiResponse.data as any;
        const fsvApiResponseObj: FsvApiResponse = fsvResponseData.result
          ? fsvResponseData as FsvApiResponse
          : { result: 'success', msg: null, data: fsvResponseData } as FsvApiResponse;

        fsvData = this.transformFSVData(fsvApiResponseObj);
        dashLog('FSV data transformed from API', {
          amountRaw: fsvData.amountRaw,
          haircutPercentage: fsvData.haircutPercentage,
          policyLabel: fsvData.policyLabel
        });
      } catch (error) {
        dashError('Failed to transform FSV data, using fallback', { error });
        // Fallback to calculated values
        const haircutPercentage = 30;
        const fsvAmount = latestPredict.predict_full * (1 - haircutPercentage / 100);
        fsvData = {
          amount: formatIDR(fsvAmount),
          amountRaw: fsvAmount,
          currency: 'IDR',
          haircutPercentage,
          policyLabel: 'Standard Land / Residential',
          haircutDescription: 'Land with medium liquidity profile',
          mainText: 'Moderate liquidity profile. Forced sale value reflects standard market risk.',
          policyName: 'Standard Land / Residential'
        };
      }
    } else {
      // Fallback: calculate FSV from market value with 30% haircut
      dashLog('FSV data not available, using fallback calculation');
      const haircutPercentage = 30;
      const fsvAmount = latestPredict.predict_full * (1 - haircutPercentage / 100);
      fsvData = {
        amount: formatIDR(fsvAmount),
        amountRaw: fsvAmount,
        currency: 'IDR',
        haircutPercentage,
        policyLabel: 'Standard Land / Residential',
        haircutDescription: 'Land with medium liquidity profile',
        mainText: 'Moderate liquidity profile. Forced sale value reflects standard market risk.',
        policyName: 'Standard Land / Residential'
      };
    }

    // Transform Reliability data from API if available
    let reliabilityData: ReliabilityData | undefined;
    dashLog('processing reliability API response', {
      hasResponse: !!reliabilityApiResponse,
      hasData: !!reliabilityApiResponse?.data
    });

    if (reliabilityApiResponse?.data) {
      try {
        // Unwrap the response if it's wrapped
        const reliabilityResponseData = reliabilityApiResponse.data as any;
        dashLog('reliability response data structure', {
          hasResult: !!reliabilityResponseData.result,
          hasData: !!reliabilityResponseData.data,
          keys: Object.keys(reliabilityResponseData),
          rawData: JSON.stringify(reliabilityResponseData).substring(0, 500)
        });

        const reliabilityApiResponseObj: ReliabilityApiResponse = reliabilityResponseData.result
          ? reliabilityResponseData as ReliabilityApiResponse
          : { result: 'success', msg: null, data: reliabilityResponseData } as ReliabilityApiResponse;

        reliabilityData = this.transformReliabilityData(reliabilityApiResponseObj);
        dashLog('Reliability data transformed from API', {
          score: reliabilityData.score.value,
          level: reliabilityData.score.level,
          factorsCount: reliabilityData.factors.length,
          title: reliabilityData.title,
          subtitle: reliabilityData.subtitle
        });
      } catch (error) {
        dashError('Failed to transform Reliability data', { error, message: (error as Error)?.message, stack: (error as Error)?.stack });
        // Don't set reliabilityData, it will remain undefined
      }
    } else {
      dashWarn('Reliability API response data not available');
    }

    const perSqmExact = latestPredict.predict_per_sqm || 0;
    // Используем predict_99 для High/Low (total и per m²) - берём последнюю дату
    const totalHighRaw = latestPredict.predict_99?.upper_bound || upperBoundTotal;
    const totalLowRaw = latestPredict.predict_99?.lower_bound || lowerBoundTotal;
    const perSqmHighRaw = latestPredict.predict_99?.upper_bound_per_sqm || 0;
    const perSqmLowRaw = latestPredict.predict_99?.lower_bound_per_sqm || 0;
    // Для 95% H и 95% L используем значения из predict_95 (за м²)
    const perSqmHigh95Raw = latestPredict.predict_95?.upper_bound_per_sqm || 0;
    const perSqmLow95Raw = latestPredict.predict_95?.lower_bound_per_sqm || 0;

    // Extract land area from regInfo or from marketPayload raw data
    let landAreaRaw = 0;
    if (regInfo?.size) {
      landAreaRaw = Number(regInfo.size) || 0;
    } else if (latestPredict?.raw && typeof latestPredict.raw === 'object' && !Array.isArray(latestPredict.raw)) {
      // Try to extract from market data raw (raw is an object with land_size property)
      const rawObj = latestPredict.raw as any;
      if (rawObj?.land_size) {
        landAreaRaw = Number(rawObj.land_size) || 0;
      }
    }
    const landAreaFormatted = `${new Intl.NumberFormat('id-ID').format(landAreaRaw)} m²`;
    // const volumeIndex = marketPayload.indexes?.['vol'];
    // const volumePercent = typeof volumeIndex === 'number' ? volumeIndex : null;

    // Extract volatility from volatilityApiResponse
    const volatility = volatilityApiResponse?.data?.volatility ?? null;

    // Map history for the chart
    // Сначала сортируем по дате (от старых к новым), затем берем последние 12 элементов
    // Используем значения за квадратный метр вместо полной стоимости
    const history = marketPayload.predict
      .map(item => {
        // Parse the date YYYY-MM-DD
        return {
          date: item.date,
          value: item.predict_per_sqm,
          lower: item.predict_95?.lower_bound_per_sqm || (item.predict_per_sqm * 0.9), // fallback if missing
          upper: item.predict_95?.upper_bound_per_sqm || (item.predict_per_sqm * 1.1)  // fallback if missing
        };
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()) // Сортируем по дате
      .slice(-12); // Берем последние 12 элементов (от последней имеющейся даты)

    return {
      uuid,
      marketValue: {
        value: formatIDR(latestPredict.predict_full),
        valueRaw: latestPredict.predict_full,
        currency: 'IDR',
        landArea: landAreaFormatted,
        landAreaRaw,
        perSqmExact: formatIDR(perSqmExact),
        perSqmExactRaw: perSqmExact,
        valueHigh: formatIDR(totalHighRaw),
        valueHighRaw: totalHighRaw,
        valueLow: formatIDR(totalLowRaw),
        valueLowRaw: totalLowRaw,
        perSqmHigh: formatIDR(perSqmHighRaw),
        perSqmHighRaw,
        perSqmLow: formatIDR(perSqmLowRaw),
        perSqmLowRaw,
        perSqmHigh95: formatIDR(perSqmHigh95Raw),
        perSqmHigh95Raw,
        perSqmLow95: formatIDR(perSqmLow95Raw),
        perSqmLow95Raw,
        volatility,
        history
      },
      subjectProperty: {
        title: location?.regency?.name ? `Subject land parcel – ${location.regency.name}` : 'Subject land parcel',
        area: landAreaFormatted,
        category: regInfo?.hakType ? `Land (${regInfo.hakType})` : 'Land',
        address: location ? `${location.kecamatan?.name || ''}, ${location.regency?.name || ''}`.replace(/^,\s*|,\s*$/g, '') : '',
        coordinates: regInfo?.centerCoord || [0, 0]
      },
      polygonCoordinates: polygonCoordinates || undefined,
      fsv: fsvData,
      reliability: reliabilityData,
      reliabilityMeta: {
        comparablesCount,
        priceSpreadPercentage
      },
      comparables: comparables.length > 0 ? comparables : undefined,
      comparablePropertiesWarning,
      isochrone: isochroneData || null
    };
  }

  /**
   * Обработка результатов поиска
   */
  private processSearchResults(results: SearchResult<SearchApiItem>[]): SearchPlotItem[] {
    dashLog('processing search results', {
      isArray: Array.isArray(results),
      count: Array.isArray(results) ? results.length : 0,
    });

    if (!Array.isArray(results)) {
      dashWarn('search results is not an array', {});
      return [];
    }

    // Сначала ищем реальные участки (type: 'plot')
    const realPlotItems = results
      .filter((result: SearchResult<SearchApiItem>) => {
        return isSearchPlotItem(result.data);
      })
      .map((result: SearchResult<SearchApiItem>) => {
        const plotItem = result.data as SearchPlotItem;
        return plotItem;
      });

    if (realPlotItems.length > 0) {
      dashLog('returning plot items', { count: realPlotItems.length });
      return realPlotItems;
    }

    dashWarn('no plot items found in search results', {});
    return [];
  }
}
